import {
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Grid2,
	Snackbar,
	SnackbarCloseReason,
	TextField,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { useMutation } from "@tanstack/react-query";
import moment from "moment";
import * as React from "react";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { ErrorModal } from "../../../../../components/modals/error-modal";
import MesfModal from "../../../../../components/modals/modal.mesf";
import { Alert } from "../../../../../controls/alert";
import { ErrorLabel } from "../../../../../controls/error-label";
import { HelmetDexteel } from "../../../../../helmet/HelmetDexteel";
import { INITIAL_VALUES } from "../../models/FormInitialValues";
import { ShiftParameters } from "../../models/ShiftParameters";
import { deleteShiftParameters } from "../../repositories/ShiftsCrewsRepository";

type Props = {
	shiftSelected: ShiftParameters | null;
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	suffixTitle?: string;
	assetId: number | null;
	assetName: string;
};

export const DeleteShift = ({
	shiftSelected,
	show,
	onHide,
	suffixTitle,
	assetId,
	assetName,
}: Props) => {
	const [open, setOpen] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [deleteAlert, setDeleteAlert] = useState(false);
	const [error, setError] = useState("");

	const {
		control,
		setValue,
		reset,
		watch,
		formState: { errors },
	} = useForm<ShiftParameters>({ defaultValues: INITIAL_VALUES });

	const deleteShift = useMutation({
		mutationFn: (patternStart: Date) =>
			deleteShiftParameters(assetId, patternStart),
		onSuccess: () => {
			setOpen(true);
			onHide(true);
		},
		onError: (error: Error) => {
			setError(error.message);
		},
		onSettled: () => {
			setIsSubmitLoading(false);
			setDeleteAlert(false);
		},
	});

	const onDelete: (data: Date) => Promise<void> = async (data: Date) => {
		setIsSubmitLoading(true);

		await deleteShift.mutate(data as Date);
	};

	const handleClose = (
		event?: React.SyntheticEvent | Event,
		reason?: SnackbarCloseReason,
	) => {
		if (reason === "clickaway") {
			return;
		}
		setOpen(false);
		setDeleteAlert(false);
	};

	const patternStart = watch("PatternStart");
	const shiftsPerDay = watch("ShiftsPerDay");
	const crewRotation = watch("CrewRotation");
	const numberOfCrews = watch("NumberOfCrews");
	const crewsCodes = watch("Crews");
	const rotationPattern = watch("PatternDays");

	useEffect(() => {
		if (show) {
			reset();
			setValue("PatternStart", shiftSelected?.PatternStart ?? new Date());
			setValue("ShiftCodes", shiftSelected?.ShiftCodes ?? "");
			setValue("Crews", shiftSelected?.Crews ?? "");
			setValue("PatternDays", shiftSelected?.PatternDays ?? 0);
			setValue("CrewRotation", shiftSelected?.CrewRotation ?? "");
			setValue("Comments", shiftSelected?.Comments ?? "");
		}
	}, [show]);

	return (
		<>
			{show && (
				<HelmetDexteel
					title={`Delete Shift${suffixTitle ? ` - ${suffixTitle}` : ""}`}
				/>
			)}
			<MesfModal
				title="DELETE SHIFT"
				open={show}
				handleClose={() => onHide(false)}
				id="delete-shift-MesfModal"
				maxWidth="md"
			>
				<form>
					<MesfModal.Content>
						<Grid2 container spacing={2} p={1}>
							<Grid2 size={{ md: 12, xs: 12 }}>
								<TextField
									label="Asset"
									value={assetName}
									disabled
									fullWidth
									variant="outlined"
									margin="dense"
								/>
							</Grid2>
							<Controller
								name="PatternStart"
								control={control}
								render={({ field: { value, onChange } }) => (
									<LocalizationProvider dateAdapter={AdapterMoment}>
										<Grid2 size={{ md: 6, xs: 12 }}>
											<DatePicker
												format="MM/DD/YYYY"
												sx={{
													minWidth: "100%",
													borderTopLeftRadius: 3,
													borderTopRightRadius: 3,
												}}
												label="Start"
												value={value ? moment(value) : value}
												onChange={(evt: any) => onChange(evt?.toDate())}
												slotProps={{
													textField: {
														fullWidth: true,
														variant: "standard",
														disabled: true,
													},
												}}
											/>
										</Grid2>
										<Grid2 size={{ md: 6, xs: 12 }}>
											<DatePicker
												label="Time"
												value={value ? moment(value) : value}
												onChange={(evt: any) => onChange(evt?.toDate())}
												slotProps={{
													textField: {
														fullWidth: true,
														variant: "standard",
														disabled: true,
													},
												}}
											/>
										</Grid2>
									</LocalizationProvider>
								)}
							/>

							<Grid2 size={{ md: 6, xs: 6 }}>
								<Controller
									name="ShiftsPerDay"
									control={control}
									rules={{
										min: 1,
									}}
									render={({ field: { onChange, value } }) => (
										<TextField
											label={`# ${
												shiftsPerDay > 1 ? "Shifts" : "Shift"
											}  per day`}
											variant="outlined"
											error={!!errors.ShiftsPerDay}
											fullWidth
											margin="dense"
											autoComplete="off"
											type="number"
											value={crewRotation.length / rotationPattern}
											disabled={!!shiftSelected}
										/>
									)}
								/>
								{errors.ShiftsPerDay?.type === "min" && (
									<ErrorLabel text="Shift per day is required" />
								)}
							</Grid2>
							<Grid2 size={{ md: 6, xs: 6 }} style={{ paddingBottom: "0" }}>
								<Controller
									name="ShiftCodes"
									control={control}
									render={({ field: { value } }) => (
										<TextField
											label="Shifts Ids"
											variant="outlined"
											error={!!errors.ShiftCodes}
											fullWidth
											margin="dense"
											autoComplete="off"
											value={value}
											disabled={!!shiftSelected}
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{ md: 6, xs: 6 }}>
								<Controller
									name="NumberOfCrews"
									control={control}
									rules={{
										min: 1,
										max: 9,
									}}
									render={({ field: { value } }) => (
										<TextField
											label={`# ${numberOfCrews > 1 ? "Crews" : "Crew"}`}
											variant="outlined"
											error={!!errors.NumberOfCrews}
											fullWidth
											margin="dense"
											autoComplete="off"
											type="number"
											value={crewsCodes.length}
											disabled={!!shiftSelected}
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{ md: 6, xs: 6 }} style={{ paddingBottom: "0" }}>
								<Controller
									name="Crews"
									control={control}
									render={({ field: { value } }) => (
										<TextField
											label="Crews Ids"
											variant="outlined"
											error={!!errors.Crews}
											fullWidth
											margin="dense"
											autoComplete="off"
											value={value}
											disabled={!!shiftSelected}
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{ md: 6, xs: 6 }}>
								<Controller
									name="PatternDays"
									control={control}
									render={({ field: { value } }) => (
										<TextField
											label="Rotation Pattern"
											variant="outlined"
											error={!!errors.PatternDays}
											fullWidth
											type="number"
											margin="dense"
											autoComplete="off"
											value={value}
											disabled={!!shiftSelected}
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{ md: 12, xs: 12 }}>
								<Controller
									name="CrewRotation"
									control={control}
									render={({ field: { value, onChange } }) => (
										<TextField
											label="Crew rotation"
											variant="outlined"
											fullWidth
											margin="dense"
											autoComplete="off"
											value={value}
											disabled={!!shiftSelected}
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{ md: 12, xs: 12 }}>
								<Controller
									name="Comments"
									control={control}
									render={({ field: { value, onChange } }) => (
										<TextField
											style={{ margin: "8px 0" }}
											size="medium"
											maxRows={5}
											label="Comments"
											multiline
											variant="outlined"
											fullWidth
											autoComplete="off"
											value={value}
											disabled={!!shiftSelected}
										/>
									)}
								/>
							</Grid2>
						</Grid2>
					</MesfModal.Content>
					<MesfModal.Actions>
						<Grid2 container spacing={2} justifyContent="flex-end">
							<Grid2 size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
								<Button
									fullWidth
									variant="contained"
									color="inherit"
									onClick={() => onHide(false)}
								>
									Cancel
								</Button>
							</Grid2>
							<Grid2 size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
								<Button
									fullWidth
									startIcon={
										isSubmitLoading && <CircularProgress size="1rem" />
									}
									disabled={
										isSubmitLoading
											? isSubmitLoading
											: shiftSelected
												? new Date(shiftSelected.PatternStart) < new Date() &&
													true
												: false
									}
									variant="contained"
									color="secondary"
									onClick={() => setDeleteAlert(true)}
								>
									Delete
								</Button>
								{shiftSelected &&
									new Date(shiftSelected?.PatternStart) < new Date() && (
										<Grid2>
											<ErrorLabel text="Can not delete before the current date" />
										</Grid2>
									)}
							</Grid2>
						</Grid2>
					</MesfModal.Actions>
					<Dialog
						open={deleteAlert}
						onClose={() => handleClose()}
						aria-labelledby="responsive-dialog-title"
					>
						<DialogTitle id="responsive-dialog-title">{"WARNING"}</DialogTitle>
						<DialogContent>
							<DialogContentText style={{ padding: 0, margin: 0 }}>
								Be careful, once deleted you will not be able to recover this
								shift, are you sure you want to delete it?
							</DialogContentText>
						</DialogContent>
						<DialogActions style={{ padding: "16px 24px" }}>
							<Button
								onClick={() => setDeleteAlert(false)}
								autoFocus
								color="inherit"
							>
								CANCEL
							</Button>
							<Button
								startIcon={isSubmitLoading && <CircularProgress size="1rem" />}
								autoFocus
								color="secondary"
								onClick={() => onDelete(patternStart as Date)}
							>
								Delete
							</Button>
						</DialogActions>
					</Dialog>
				</form>
			</MesfModal>
			<ErrorModal error={error} onHide={() => setError("")} />
			<Snackbar
				open={open}
				autoHideDuration={2500}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert severity="warning" onClose={handleClose}>
					The shift was deleted successfully
				</Alert>
			</Snackbar>
		</>
	);
};
